import React, { useState, useEffect } from 'react';

const ProductBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const products = [
    {
      id: 0,
      name: "Featured",
      subtitle: "Premium Swedish Candy",
      description: "Discover our authentic Swedish-style confections",
      video: "https://customer-assets.emergentagent.com/job_kato-mito-upgrade/artifacts/z7vwrhaa_copy_90A1C647-4601-4049-A49C-55437AA9F598.mov",
      bgColor: "from-gray-900 to-gray-800",
      textColor: "text-white",
      isVideo: true
    }
  ];

  // Auto-rotate every 5 seconds for better synchronization
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [products.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % products.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
  };

  const currentProduct = products[currentSlide];

  return (
    <section 
      className={`relative h-[600px] bg-gradient-to-r ${currentProduct.bgColor} overflow-hidden transition-all duration-1000`}
    >
      {/* Video Background (if video slide) */}
      {currentProduct.isVideo && currentProduct.video && (
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src={currentProduct.video} type="video/mp4" />
          <source src={currentProduct.video} type="video/quicktime" />
          Your browser does not support the video tag.
        </video>
      )}
      
      {/* Background Pattern (for non-video slides) */}
      {!currentProduct.isVideo && (
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23FFFFFF%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%227%22 cy=%227%22 r=%223%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full relative">
        {/* Heart Candies Background for Kärlek */}
        {currentProduct.heartCandies && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {currentProduct.heartCandies.map((heart) => (
              <div
                key={heart.id}
                className={`absolute ${heart.position} animate-heartPop opacity-0`}
                style={{
                  animationDelay: heart.delay,
                  animationFillMode: 'forwards',
                  zIndex: 1
                }}
              >
                <img
                  src={heart.image}
                  alt={heart.type === 'heart' ? 'Love Heart Candy' : heart.type === 'butterfly' ? 'Sour Butterfly Candy' : 'Sour Bear Candy'}
                  className={heart.type === 'heart' 
                    ? "w-12 h-12 md:w-16 md:h-16 opacity-75 hover:opacity-90 transition-opacity duration-300"
                    : heart.type === 'butterfly'
                    ? "w-24 h-24 md:w-32 md:h-32 opacity-90 hover:opacity-100 transition-opacity duration-300"
                    : "w-20 h-20 md:w-28 md:h-28 opacity-85 hover:opacity-100 transition-opacity duration-300"
                  }
                  style={{
                    filter: heart.type === 'butterfly' 
                      ? 'drop-shadow(0 3px 8px rgba(0,0,0,0.2)) brightness(1.1) contrast(1.1)' 
                      : heart.type !== 'heart' 
                      ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))' 
                      : 'none'
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Candy Shapes Background for Glädje */}
        {currentProduct.candyShapes && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {currentProduct.candyShapes.map((candy) => (
              <div
                key={candy.id}
                className={`absolute ${candy.position} animate-heartPop opacity-0`}
                style={{
                  animationDelay: candy.delay,
                  animationFillMode: 'forwards',
                  zIndex: 1
                }}
              >
                <img
                  src={candy.image}
                  alt={`Joyful Swedish Candy - ${candy.type || 'Mixed'}`}
                  className="w-20 h-20 md:w-28 md:h-28 opacity-85 hover:opacity-100 transition-opacity duration-300 object-contain"
                  style={{
                    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.15))'
                  }}
                />
              </div>
            ))}
          </div>
        )}
        
        {currentProduct.products ? (
          /* Special Layout for 3rd Slide - Text Left/Right, Images Center */
          <div className="flex items-center justify-between h-full relative z-10">
            {/* Left Product - Jönköping */}
            <div className="flex-1 text-white animate-slideInLeft">
              <div className="max-w-md pr-8">
                <h2 className="text-4xl md:text-5xl font-black mb-4 leading-none">
                  {currentProduct.products[0].name}
                </h2>
                <p className="text-lg mb-6 opacity-90 leading-relaxed">
                  {currentProduct.products[0].description}
                </p>
                <a href="/products" className="bg-white text-gray-800 px-6 py-3 rounded-full text-base font-black hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl hover:scale-105 inline-block">
                  EXPLORE JÖNKÖPING
                </a>
              </div>
            </div>

            {/* Center - Product Images */}
            <div className="flex-1 flex justify-center items-center animate-fadeInUp">
              <div className="flex gap-4 items-center">
                {currentProduct.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`${currentProduct.products[index]?.name || currentProduct.name} ${index + 1}`}
                      className="h-72 w-auto object-contain hover:scale-110 transition-all duration-500 drop-shadow-2xl"
                      style={{ animationDelay: `${index * 0.2}s` }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Product - Stockholm */}
            <div className="flex-1 text-white animate-slideInRight">
              <div className="max-w-md pl-8">
                <h2 className="text-4xl md:text-5xl font-black mb-4 leading-none">
                  {currentProduct.products[1].name}
                </h2>
                <p className="text-lg mb-6 opacity-90 leading-relaxed">
                  {currentProduct.products[1].description}
                </p>
                <a href="/products" className="bg-white text-gray-800 px-6 py-3 rounded-full text-base font-black hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl hover:scale-105 inline-block">
                  EXPLORE STOCKHOLM
                </a>
              </div>
            </div>
          </div>
        ) : currentProduct.isVideo ? (
          /* Video Slide - Logo Overlay on Video */
          <div className="flex items-center justify-center h-full relative z-10">
            <div className="text-center max-w-2xl mx-auto px-4 relative z-10 animate-fadeInUp">
              <img 
                src="https://customer-assets.emergentagent.com/job_kato-mito-upgrade/artifacts/llbvcjnh_IMG_0805.png" 
                alt="Kato Mito" 
                className="w-80 md:w-96 h-auto mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        ) : (
          /* Standard Layout for 1st and 2nd Slides */
          <div className="flex items-center justify-between h-full relative z-10">
            {/* Left Content */}
            <div 
              key={`content-${currentSlide}`}
              className={`flex-1 ${currentProduct.textColor} animate-slideInLeft`}
            >
              <div className="max-w-2xl">
                <div className="mb-4">
                  <span className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-lg font-bold mb-4 animate-fadeInUp">
                    🍭 NEW FLAVOR ALERT
                  </span>
                </div>
                
                <h2 className="text-5xl md:text-6xl font-black mb-3 leading-none animate-fadeInUp">
                  {currentProduct.name}
                </h2>
                
                <p className="text-xl md:text-2xl font-bold mb-4 opacity-90 animate-fadeInUp">
                  {currentProduct.subtitle}
                </p>
                
                <p className="text-lg mb-6 opacity-80 leading-relaxed animate-fadeInUp">
                  {currentProduct.description}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3 animate-fadeInUp">
                  <a href="/contact#quote" className="bg-white text-gray-800 px-6 py-3 rounded-full text-base font-black hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl hover:scale-105">
                    REQUEST SAMPLES
                  </a>
                  <a href="/products" className="border-2 border-white text-white px-6 py-3 rounded-full text-base font-black hover:bg-white hover:text-gray-800 transition-all duration-300 hover:scale-105 inline-block text-center">
                    VIEW DETAILS
                  </a>
                </div>
              </div>
            </div>

            {/* Right Content - Single Product Image */}
            <div 
              key={`image-${currentSlide}`}
              className="flex-1 flex justify-center items-center animate-slideInRight"
            >
              <div className="relative group">
                <img
                  src={currentProduct.image}
                  alt={currentProduct.name}
                  className="h-80 w-auto object-contain hover:scale-110 transition-all duration-500 drop-shadow-2xl animate-fadeInUp"
                  key={currentProduct.id}
                />
              </div>
            </div>
          </div>
        )}
      </div>



      {/* Slide Indicators - Only show if more than 1 slide */}
      {products.length > 1 && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentSlide 
                  ? 'bg-white scale-125' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      )}

      {/* Product Counter - Only show if more than 1 slide */}
      {products.length > 1 && (
        <div className="absolute top-6 right-6 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full font-bold">
          {currentSlide + 1} / {products.length}
        </div>
      )}

      {/* Subtle Wave Bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-4" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M0,120L1200,120L1200,20C800,60 400,60 0,20Z" className="fill-white"></path>
        </svg>
      </div>
    </section>
  );
};

export default ProductBanner;